/**
 * SceneCharsSection - Non-party characters present in the current scene.
 *
 * Rendered inside ScenePanel below the description. Shows NPCs, monsters,
 * and animals with health/attitude pills, conditions, and concentration markers.
 * Hidden when no scene characters are present.
 */

import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ConditionBadge } from '@/components/ui/condition-badge';
import { StatusPill } from '@/components/ui/status-pill';
import ImageModal from '@/components/common/ImageModal';
import { ConcentrationPill } from '@/components/shared/ConcentrationPill';
import { Skull, Swords, User } from 'lucide-react';
import { useGameStore, deriveHealthState, visibleConditions } from '@/lib/stores/gameStore';
import { CharacterTypeEnum } from '@danda/shared';
import type { HealthState } from '@danda/shared';
import { isHiddenSceneCorpse } from './scene-chars-helpers';

function getCharacterIcon(type?: 'pc' | 'npc' | 'monster' | 'animal') {
  switch (type) {
    case CharacterTypeEnum.MONSTER:
      return <Skull className="h-3 w-3 text-red-500" />;
    case CharacterTypeEnum.ANIMAL:
      return <Swords className="h-3 w-3 text-amber-500" />;
    case CharacterTypeEnum.NPC:
    default:
      return <User className="h-3 w-3 text-blue-500" />;
  }
}

const healthStateBadgeConfig: Record<HealthState, { label: string; className: string }> = {
  uninjured: { label: 'UNINJURED', className: 'bg-green-600 text-white' },
  wounded: { label: 'WOUNDED', className: 'bg-yellow-600 text-white' },
  bloodied: { label: 'BLOODIED', className: 'bg-orange-600 text-white' },
  critical: { label: 'CRITICAL', className: 'bg-red-600 text-white' },
  dying: { label: 'DYING', className: 'bg-red-900 text-white' },
  dead: { label: 'DEAD', className: 'bg-gray-800 text-white' },
};

function getAttitudeBadge(attitude?: 'hostile' | 'unfriendly' | 'neutral' | 'friendly' | 'allied') {
  switch (attitude) {
    case 'hostile':
      return { label: 'HOSTILE', className: 'bg-red-600 text-white' };
    case 'unfriendly':
      return { label: 'UNFRIENDLY', className: 'bg-orange-600 text-white' };
    case 'friendly':
      return { label: 'FRIENDLY', className: 'bg-green-600 text-white' };
    case 'allied':
      return { label: 'ALLIED', className: 'bg-blue-600 text-white' };
    case 'neutral':
    default:
      return { label: 'NEUTRAL', className: 'bg-gray-600 text-white' };
  }
}

export default function SceneCharsSection() {
  const { sceneCharacters, currentTurn } = useGameStore(
    useShallow((s) => ({
      sceneCharacters: s.sceneCharacters,
      currentTurn: s.currentTurn,
    }))
  );
  const [portraitOverlay, setPortraitOverlay] = useState<{ url: string; name: string } | null>(
    null
  );

  // #248: a dead monster/animal is a corpse, not an active scene character —
  // drop it from this panel (there is no loot system, so there's no "items
  // panel" copy; it's simply no longer a combatant). A dead NPC stays visible:
  // a named NPC death is narratively relevant, not loot clutter.
  const sortedChars = [...sceneCharacters]
    .filter((char) => {
      const hpCurrent = char.hp ?? char.maxHp ?? 10;
      const hpMax = char.maxHp ?? 10;
      const healthState = char.healthState ?? deriveHealthState(hpCurrent, hpMax, char.conditions);
      return !isHiddenSceneCorpse(healthState, char.type);
    })
    .sort((a, b) => {
      const typeOrder: Record<string, number> = { monster: 0, npc: 1, animal: 2 };
      const aOrder = typeOrder[a.type ?? 'npc'] ?? 1;
      const bOrder = typeOrder[b.type ?? 'npc'] ?? 1;
      if (aOrder !== bOrder) return aOrder - bOrder;
      if (a.initiative !== undefined && b.initiative !== undefined) {
        return b.initiative - a.initiative;
      }
      return a.name.localeCompare(b.name);
    });

  if (sortedChars.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {sortedChars.map((char) => {
        const isCurrentTurn = char.charId === currentTurn;
        const hpCurrent = char.hp ?? char.maxHp ?? 10;
        const hpMax = char.maxHp ?? 10;
        const healthState =
          char.healthState ?? deriveHealthState(hpCurrent, hpMax, char.conditions);
        const healthBadge = healthStateBadgeConfig[healthState];
        const attitudeBadge = getAttitudeBadge(char.attitude);

        return (
          <div
            key={char.charId}
            className={`p-2 border rounded-lg transition-all ${
              isCurrentTurn ? 'border-accent-gold bg-accent-gold/10 shadow-md' : 'border-border'
            }`}
          >
            <div className="flex items-center gap-2">
              <Avatar
                className={`h-8 w-8 ${char.portraitUrl ? 'cursor-pointer' : ''}`}
                onClick={
                  char.portraitUrl
                    ? () => setPortraitOverlay({ url: char.portraitUrl!, name: char.name })
                    : undefined
                }
              >
                {char.avatarUrl && <AvatarImage src={char.avatarUrl} alt={char.name} />}
                <AvatarFallback className="text-xs">
                  {char.avatarEmoji || getCharacterIcon(char.type)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="font-medium text-sm truncate font-character">{char.name}</span>
                  <StatusPill size="xs" className={healthBadge.className}>
                    {healthBadge.label}
                  </StatusPill>
                </div>
                {char.species && (
                  <p className="text-[10px] text-muted-foreground truncate uppercase tracking-wide">
                    {char.class ? `${char.species} ${char.class}` : char.species}
                  </p>
                )}
                <div className="flex items-center flex-wrap gap-1 mt-0.5">
                  <StatusPill size="xs" className={attitudeBadge.className}>
                    {attitudeBadge.label}
                  </StatusPill>
                  {char.isConcentrating && (
                    <ConcentrationPill
                      isConcentrating={char.isConcentrating}
                      spellName={char.concentrationSpell}
                    />
                  )}
                  {visibleConditions(char.conditions, healthState).map((condition) => (
                    <ConditionBadge
                      key={typeof condition === 'string' ? condition : condition.name}
                      condition={condition}
                      size="xs"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <ImageModal
        isOpen={!!portraitOverlay}
        onClose={() => setPortraitOverlay(null)}
        imageUrl={portraitOverlay?.url}
        altText={portraitOverlay?.name ?? ''}
      />
    </div>
  );
}
